import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

import {
  PAYPAL_FEE_PCT,
  fmtEUR, uid, daysBetween, dateAdd,
} from "./builder/utils";
import { usePartners } from "@/lib/partners";
import { Field, Row } from "./builder/atoms";
import { AccommodationsBlock, RoomConfigEditor } from "./builder/AccommodationsBlock";
import { DayBlock } from "./builder/DayBlock";
import { DayGap } from "./builder/DayGap";
import { OrientationModal } from "./builder/OrientationModal";
import { FxConverter } from "./builder/FxConverter";
import { PartnerSelector } from "./builder/PartnerSelector";
import { SalesAgentSelector } from "./builder/SalesAgentSelector";
import { OwnerSelector } from "./builder/OwnerSelector";
import { SofiPushModal } from "./builder/SofiPushModal";
// ShareItineraryModal deprecated 2026-07-14 — all agents now see every
// itinerary by default (see Dashboard filter). The modal file is kept for
// history but no longer imported.
import { PaymentLinkModal } from "./builder/PaymentLinkModal";
import { ExtrasModal } from "./builder/ExtrasModal";
import { RefundsModal } from "./builder/RefundsModal";
import { PostSaleSection } from "./builder/PostSaleSection";
import { CashflowStatus } from "./builder/CashflowStatus";
import { RotateCw, ExternalLink, Eye, Send, Moon, CreditCard, Sparkles, Undo2, ChevronDown, ChevronUp } from "lucide-react";

export default function ItineraryBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [itn, setItn] = useState(null);
  const { defaults: partnerDefaultsOf, label: partnerLabelOf } = usePartners();
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const [facets, setFacets] = useState({ countries: [], cities: [], types: [] });
  const [activeDayId, setActiveDayId] = useState(null);
  const dragRef = useRef(null);
  // Day drag-and-drop for reordering. Must live at the top level (before
  // any conditional return) to comply with react-hooks/rules-of-hooks.
  const dayDragRef = useRef(null);

  // Sofi push modal state. `dryRun` flag controls whether the modal opens in
  // preview mode (fills the form, captures screenshot, no submit) or real-push
  // mode. Closed by default.
  const [sofiModal, setSofiModal] = useState({ open: false, dryRun: true });
  // PayPal payment-link modal state. Lazy-generates / refreshes the public
  // /pay/:token link the agent sends to the client.
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  // Post-sale extras + refund workflow modals — always available (not
  // gated on itinerary.status="sold") so agents can start capturing
  // add-ons and cancellations at any time.
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [refundsModalOpen, setRefundsModalOpen] = useState(false);
  // Payment-recovery banner: only surfaces when THIS trip has no captured
  // payments but a sibling version does — introduced 2026-09-14 after the
  // Susan Hayden incident where a v2→v3 duplication auto-moved the deposit
  // to v3 (per the 2026-08-01 dup fix), leaving the sold v2 looking empty.
  const [paymentSiblings, setPaymentSiblings] = useState([]);
  const [movingPayments, setMovingPayments] = useState(false);
  // Aside "Coste" collapse toggle — when collapsed only the PVP final is
  // shown so the estado-de-cobros + payment history are visible without
  // scrolling. Persist locally to remember the agent's preference.
  const [costCollapsed, setCostCollapsed] = useState(() => {
    try { return localStorage.getItem("vdv_cost_collapsed") === "1"; } catch { return false; }
  });
  const toggleCost = () => {
    setCostCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("vdv_cost_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Auto-open a modal when the page is opened from an email deep-link.
  // e.g. `/itineraries/xxx?open=refunds` (from refund notification emails)
  // or `?open=extras`. Runs once on mount; the flag is deliberately not
  // cleared from the URL so a refresh keeps the same view.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const which = params.get("open");
    if (which === "refunds") setRefundsModalOpen(true);
    else if (which === "extras") setExtrasModalOpen(true);
  }, []);

  // FX rate for EUR↔USD conversion. Starts from the daily ECB feed, but if
  // the itinerary already has a `fx_rate` value saved on the doc, that value
  // overrides the live feed once the itinerary loads. Agents can change the
  // rate inline (it gets persisted), or click "Auto" to clear the override
  // and revert to the live feed.
  const [fx, setFx] = useState({ rate: 1.10, source: "loading", date: "" });
  useEffect(() => {
    let alive = true;
    api.get("/fx/rate").then(({ data }) => {
      // RACE GUARD: the itinerary loader (other useEffect) may resolve first
      // and set source="manual" from the saved fx_rate. If that happened, we
      // must NOT stomp on the manual value with today's live ECB rate.
      if (alive && data && data.rate) {
        setFx((prev) => prev.source === "manual"
          ? prev
          : { rate: Number(data.rate), source: data.source, date: data.date });
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Initial itinerary load + legacy data cleanup (dash-separated cities).
  const load = useCallback(async () => {
    const { data } = await api.get(`/itineraries/${id}`);
    if ((!data.days || data.days.length === 0) && data.start_date && data.end_date) {
      const n = daysBetween(data.start_date, data.end_date);
      data.days = Array.from({ length: n }).map((_, i) => ({
        day_id: uid("day"),
        date: dateAdd(data.start_date, i),
        label: `Día ${i + 1}`,
        city: "",
        services: [],
      }));
    }
    // Multi-city is now expressed with commas — legacy dash values returned 0 results.
    let cleaned = false;
    (data.days || []).forEach((d) => {
      if (typeof d.city === "string" && d.city.includes("-")) {
        d.city = ""; cleaned = true;
      }
    });
    if (cleaned) toast.message("Limpieza automática de filtros antiguos de ciudad (formato con guión).");
    setItn(data);
    setActiveDayId((prev) => prev || data.days?.[0]?.day_id || null);
    if (typeof data.fx_rate === "number" && data.fx_rate > 0) {
      setFx({ rate: Number(data.fx_rate), source: "manual", date: "" });
    }
    // Note: if the doc has NO fx_rate, we do NOT auto-persist one. The agent
    // decides — they can type a rate into the FxConverter input or leave it
    // null to track the live ECB feed. `_fx_rate_for` on the backend falls
    // back to the same cached ECB rate the widget shows, so the builder view
    // and the payment link always agree regardless of what the agent chooses.
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Discover misplaced payments on sibling versions. Runs after each load
  // (via itn state change) so if the user pushes a new duplicate the
  // banner stays accurate. Best-effort: silent failure keeps the builder
  // usable if the endpoint is temporarily unavailable.
  useEffect(() => {
    if (!itn?.itinerary_id) return;
    let stale = false;
    (async () => {
      try {
        const { data } = await api.get(`/itineraries/${itn.itinerary_id}/payment-siblings`);
        if (!stale) setPaymentSiblings(data?.siblings || []);
      } catch { /* ignore */ }
    })();
    return () => { stale = true; };
  }, [itn?.itinerary_id, itn?.payments?.length]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/experiences/facets");
      setFacets(data);
    })();
  }, []);

  // Global orientation modal state.
  const [orient, setOrient] = useState(null);
  const openOrient = useCallback(async (opts) => {
    const { hotelName, city: cityHint, checkin, checkout, adults, onApply } = opts;
    let city = cityHint;
    if (!city && hotelName) {
      try {
        const { data } = await api.get("/hotels", { params: { q: hotelName, include_imported: true } });
        const hit = (data || []).find((h) => (h.name || "").toLowerCase() === hotelName.toLowerCase())
                 || (data || [])[0];
        if (hit && hit.city) city = hit.city;
      } catch (_e) {
        // best-effort
      }
    }
    if (!city) {
      city = window.prompt(`¿Ciudad para buscar precio orientativo${hotelName ? ' de "' + hotelName + '"' : ""}?`, "");
      if (!city) return;
    }
    setOrient({ hotelName, city, busy: true, data: null, onApply });
    try {
      const { data } = await api.get("/hotels/price-orientation", {
        params: { city, checkin, checkout, adults: adults || 2 },
        timeout: 45000,
      });
      setOrient((prev) => prev ? { ...prev, data, busy: false } : prev);
    } catch (_e) {
      toast.error("Error consultando precio orientativo");
      setOrient(null);
    }
  }, []);

  // Debounced save with the latest itinerary snapshot.
  // Save the current itinerary immediately, cancelling any pending debounce.
  // Used by `onBlur` handlers on critical fields (name, dates) so a focus
  // change always persists the edit before the user can lose context.
  const flushSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    // Re-trigger save with 0ms delay (uses the latest state in setItn closure)
    setItn((cur) => {
      if (!cur) return cur;
      (async () => {
        setSaving(true);
        try {
          await api.patch(`/itineraries/${id}`, {
            name: cur.name, main_traveler: cur.main_traveler,
            start_date: cur.start_date, end_date: cur.end_date,
            duration_days: cur.duration_days, num_travelers: cur.num_travelers,
            num_adults: cur.num_adults, num_children: cur.num_children,
            children_ages: cur.children_ages,
            travelers: cur.travelers, days: cur.days, accommodations: cur.accommodations,
            markup_pct: cur.markup_pct, commission_pct: cur.commission_pct,
            partner: cur.partner, paypal_fee: cur.paypal_fee, currency: cur.currency, status: cur.status,
            room_config: cur.room_config, fx_rate: cur.fx_rate, notes: cur.notes,
            sales_agent_email: cur.sales_agent_email,
            hotel_perks: Array.isArray(cur.hotel_perks) ? cur.hotel_perks : null,
          });
        } finally { setSaving(false); }
      })();
      return cur;
    });
  }, [id]);

  const schedSave = useCallback((next) => {
    setItn(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.patch(`/itineraries/${id}`, {
          name: next.name, main_traveler: next.main_traveler,
          start_date: next.start_date, end_date: next.end_date,
          duration_days: next.duration_days, num_travelers: next.num_travelers,
          num_adults: next.num_adults, num_children: next.num_children,
          children_ages: next.children_ages,
          travelers: next.travelers, days: next.days, accommodations: next.accommodations,
          markup_pct: next.markup_pct, commission_pct: next.commission_pct,
          partner: next.partner, paypal_fee: next.paypal_fee, currency: next.currency, status: next.status,
          room_config: next.room_config, fx_rate: next.fx_rate, notes: next.notes,
          sales_agent_email: next.sales_agent_email,
          hotel_perks: Array.isArray(next.hotel_perks) ? next.hotel_perks : null,
        });
      } finally { setSaving(false); }
    }, 600);
  }, [id]);

  const totals = useMemo(() => {
    if (!itn) return { sub_excl: 0, sub_incl: 0, sub_with_markup: 0, commission_eur: 0, markup_eur: 0, paypal_eur: 0, pvp: 0, iva: 0 };
    let excl = 0, incl = 0;
    (itn.days || []).forEach((d) => (d.services || []).forEach((s) => {
      // Services tied to an accommodation (`acc_id` set) are derived
      // read-only chips, NOT separate cost items. The accommodation
      // itself contributes its price in the loop below, so counting
      // the carrier service here would double-bill the hotel.
      if (s.acc_id) return;
      excl += (s.unit_price_tax_excl || 0) * (s.quantity || 0);
      incl += (s.unit_price_tax_incl || s.unit_price || 0) * (s.quantity || 0);
    }));
    (itn.accommodations || []).forEach((a) => {
      excl += a.price_tax_excl || 0;
      incl += a.price_tax_incl || a.price || 0;
    });
    const mk = (itn.markup_pct || 0) / 100;
    const com_pct = (itn.commission_pct || 0);
    const markup_eur = incl * mk;
    const sub_with_markup = incl + markup_eur;
    // Gross-up: the partner deducts com_pct from the FINAL sale price, so to
    // net `sub_with_markup` on our side we sell at:
    //   pvp_pre_paypal = sub_with_markup / (1 - com_pct/100)
    // which is equivalent to:
    //   commission_eur = sub_with_markup × com_pct / (100 − com_pct)
    //   pvp_pre_paypal = sub_with_markup + commission_eur
    // The older naive `sub_with_markup × com_pct/100` formula made the
    // agency net less than the desired markup on every Zicasso / RT / Baboo
    // trip because the partner cut was applied to the final price, not the
    // markup.
    const commission_eur = com_pct ? (sub_with_markup * com_pct / (100 - com_pct)) : 0;
    const pvp_pre_paypal = sub_with_markup + commission_eur;
    // PayPal processing fee: +3% on the otherwise final PVP, paid by the
    // client to cover PayPal's cut. Toggleable per itinerary.
    const paypal_eur = itn.paypal_fee ? pvp_pre_paypal * (PAYPAL_FEE_PCT / 100) : 0;
    const pvp = pvp_pre_paypal + paypal_eur;
    // Post-sale accounting: extras already paid by the client (kind="extra"
    // captures) add to the trip's realised PVP; refunds already executed
    // through the manager-approval flow subtract from it. `pvp_adjusted`
    // is what should ultimately reach Sofi / reporting for a sold trip.
    const extras_paid = (itn.extras || []).reduce(
      (s, ex) => s + (ex.status === "paid" ? (ex.paid_amount ?? ex.amount_eur ?? 0) : 0),
      0
    );
    const refunds_executed = (itn.refund_requests || []).reduce(
      (s, rr) => s + (rr.status === "executed" ? (rr.amount_eur ?? 0) : 0),
      0
    );
    const net_adjustments = extras_paid - refunds_executed;
    const pvp_adjusted = pvp + net_adjustments;
    return {
      sub_excl: excl, sub_incl: incl, sub_with_markup, markup_eur,
      commission_eur, paypal_eur, pvp, iva: incl - excl,
      extras_paid, refunds_executed, net_adjustments, pvp_adjusted,
    };
  }, [itn]);

  if (!itn) return <div className="p-10 text-sm text-clay-700">Cargando itinerario…</div>;

  // Re-apply the partner's default markup + commission to the current itinerary.
  // Triggered by the ↻ Auto button next to the partner selector. Manual edits
  // to either field stay untouched UNTIL the agent explicitly clicks this.
  const applyPartnerDefaults = () => {
    const d = partnerDefaultsOf(itn.partner);
    if (!d) { toast.error("Partner desconocido"); return; }
    schedSave({ ...itn, markup_pct: d.markup_pct, commission_pct: d.commission_pct });
    toast.success(`Markup ${d.markup_pct}% · Comisión ${d.commission_pct}% aplicados`);
  };

  const setField = (k, v) => {
    const next = { ...itn, [k]: v };
    // NOTE: changing `partner` deliberately does NOT touch markup_pct or
    // commission_pct anymore — agents complained that their manual override
    // was silently overwritten when they revisited an itinerary. To re-apply
    // the partner's default markup+commission, use the ↻ Auto button next to
    // the partner selector (calls `applyPartnerDefaults()` below).
    if (k === "start_date" || k === "end_date") {
      const oldStart = itn.start_date || "";
      const oldEnd = itn.end_date || "";
      const newStart = k === "start_date" ? v : next.start_date;
      let newEnd = k === "end_date" ? v : next.end_date;
      const current = [...(next.days || [])];
      const msDay = 86_400_000;
      const shiftIso = (iso, deltaDays) => {
        if (!iso) return iso;
        const t = new Date(iso).getTime();
        if (isNaN(t)) return iso;
        return new Date(t + deltaDays * msDay).toISOString().slice(0, 10);
      };

      // CASE A — moving the trip: user changed `start_date` on an itinerary
      // that already has days AND an end_date. Preserve the trip length by
      // shifting end_date + every day.date + every accommodation date_from
      // and date_to by the SAME delta. Before this branch existed, changing
      // only start_date and leaving end_date stale grew the days array to
      // ~180 blank slots (Oct → Apr), which agents perceived as "everything
      // disappeared" because the original services got buried under a sea
      // of empty days. If the user wants a longer trip, they change
      // end_date next — CASE B/C picks it up and resizes properly.
      if (
        k === "start_date"
        && oldStart && newStart && oldStart !== newStart
        && current.length > 0
      ) {
        const oldStartMs = new Date(oldStart).getTime();
        const newStartMs = new Date(newStart).getTime();
        if (!isNaN(oldStartMs) && !isNaN(newStartMs)) {
          const deltaDays = Math.round((newStartMs - oldStartMs) / msDay);
          if (oldEnd) {
            const shifted = shiftIso(oldEnd, deltaDays);
            if (shifted) {
              next.end_date = shifted;
              newEnd = shifted;
            }
          }
          if (deltaDays !== 0) {
            next.accommodations = (next.accommodations || []).map((a) => ({
              ...a,
              date_from: shiftIso(a.date_from, deltaDays),
              date_to: shiftIso(a.date_to, deltaDays),
            }));
          }
          current.forEach((d, i) => { d.date = dateAdd(newStart, i); d.label = `Día ${i + 1}`; });
          next.days = current;
          next.duration_days = current.length;
          schedSave(next);
          return;
        }
      }

      // CASE B — resizing the trip: BOTH dates are set and produce a valid
      // positive day count. Grow (with blank days at the tail) or truncate
      // to match n. Only applies when end_date is the thing that changed
      // (or when the trip had no days yet — first-time initialization).
      const n = daysBetween(newStart, newEnd);
      if (n > 0) {
        next.duration_days = n;
        if (n > current.length) {
          for (let i = current.length; i < n; i++) {
            current.push({ day_id: uid("day"), date: dateAdd(newStart, i), label: `Día ${i + 1}`, city: "", services: [] });
          }
        } else if (n < current.length) {
          current.length = n;
        }
        if (k === "start_date") current.forEach((d, i) => { d.date = dateAdd(newStart, i); d.label = `Día ${i + 1}`; });
      } else if (k === "start_date" && newStart && current.length > 0) {
        // CASE C — first-time start_date on a trip whose end_date is still
        // empty (typical for a Travefy import that came without dates). Re-
        // stamp existing days without resizing so services are preserved.
        current.forEach((d, i) => { d.date = dateAdd(newStart, i); d.label = `Día ${i + 1}`; });
      }
      next.days = current;
    }
    schedSave(next);
  };

  const updateDay = (dayId, patch) =>
    schedSave({ ...itn, days: itn.days.map((d) => d.day_id === dayId ? { ...d, ...patch } : d) });

  const addDay = () => {
    const next = { ...itn };
    const i = (next.days || []).length;
    next.days = [...(next.days || []), { day_id: uid("day"), date: dateAdd(next.start_date, i), label: `Día ${i + 1}`, city: "", services: [] }];
    schedSave(next);
    setActiveDayId(next.days[next.days.length - 1].day_id);
  };

  // Re-label + re-date every day in place using the trip's start_date, so
  // labels stay "Día 1..N" contiguous after any add/remove/reorder/insert.
  const _relabelDays = (days, startDate) => {
    days.forEach((d, i) => {
      d.label = `Día ${i + 1}`;
      if (startDate) d.date = dateAdd(startDate, i);
    });
    return days;
  };

  // Insert a fresh empty day right AFTER the specified day. Every following
  // day is pushed down one position and gets re-labelled/re-dated. Used for
  // mid-trip additions (transit day, added activity block) without having
  // to rebuild the tail manually.
  const insertDayAfter = (dayId) => {
    const days = [...(itn.days || [])];
    const idx = days.findIndex((d) => d.day_id === dayId);
    if (idx === -1) return;
    days.splice(idx + 1, 0, {
      day_id: uid("day"), date: "", label: "", city: "", services: [],
    });
    schedSave({ ...itn, days: _relabelDays(days, itn.start_date) });
    setActiveDayId(days[idx + 1].day_id);
  };

  // Reorder days by drag-and-drop. `targetIdx` is the INSERTION position
  // in the original array (0 = before first, days.length = after last).
  // `dayDragRef` is declared at the top of the component with the other
  // refs to keep the hook order stable across renders.
  const onDayDragStart = (srcIdx) => { dayDragRef.current = srcIdx; };
  const onDayDrop = (targetIdx) => {
    const src = dayDragRef.current;
    dayDragRef.current = null;
    if (src == null || src === targetIdx || src + 1 === targetIdx) return;
    const days = [...(itn.days || [])];
    const [moved] = days.splice(src, 1);
    const insertAt = targetIdx > src ? targetIdx - 1 : targetIdx;
    days.splice(insertAt, 0, moved);
    schedSave({ ...itn, days: _relabelDays(days, itn.start_date) });
  };

  // Move a single day one slot up or down. Preserves accommodations by only
  // touching order + labels/dates. Mobile-friendly alternative to the
  // native HTML5 drag-and-drop (which is unusable on touch screens).
  const moveDay = (dayId, direction) => {
    const days = [...(itn.days || [])];
    const idx = days.findIndex((d) => d.day_id === dayId);
    if (idx === -1) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= days.length) return;
    [days[idx], days[target]] = [days[target], days[idx]];
    schedSave({ ...itn, days: _relabelDays(days, itn.start_date) });
  };

  const removeDay = (dayId) => {
    const next = { ...itn };
    next.days = _relabelDays((next.days || []).filter((d) => d.day_id !== dayId), next.start_date);
    schedSave(next);
  };

  // SERVICE pricing rule:
  //   For experiences: qty = num_travelers, unit_price = catalog_total / catalog_pax (= €/pax).
  //   For lodging: handled separately by upsertAccommodationFromService.
  const addServiceToDay = (dayId, partial = {}) => {
    const trav = itn.num_travelers || 1;
    const expPax = Math.max(1, parseInt(partial.pax || 1, 10));
    const type = partial.type || "actividad";
    const isLodging = type === "alojamiento";
    const incl = partial.unit_price_tax_incl ?? 0;
    const excl = partial.unit_price_tax_excl ?? incl;
    const perPaxIncl = isLodging ? incl : (incl / expPax);
    const perPaxExcl = isLodging ? excl : (excl / expPax);
    const svc = {
      service_id: uid("svc"),
      experience_id: partial.experience_id || null,
      type,
      name: partial.name || "",
      provider_name: partial.provider_name || "",
      quantity: partial.quantity ?? (isLodging ? 1 : trav),
      pax: expPax,
      unit_price_tax_excl: Math.round(perPaxExcl * 100) / 100,
      unit_price_tax_incl: Math.round(perPaxIncl * 100) / 100,
      unit_price: Math.round(perPaxIncl * 100) / 100,
      currency: partial.currency || "EUR",
    };
    schedSave({ ...itn, days: itn.days.map((d) => d.day_id === dayId ? { ...d, services: [...(d.services || []), svc] } : d) });
    setActiveDayId(dayId);
  };

  const updateService = (dayId, sid, patch) => {
    const synced = { ...patch };
    if ("unit_price_tax_incl" in synced) synced.unit_price = synced.unit_price_tax_incl;
    schedSave({
      ...itn,
      days: itn.days.map((d) =>
        d.day_id === dayId
          ? { ...d, services: d.services.map((s) => s.service_id === sid ? { ...s, ...synced } : s) }
          : d),
    });
  };

  // LODGING pricing rule (from in-day service):
  //   qty = nights × num_rooms, unit_price = avg(rooms.price_per_night)
  // so qty × unit = total. See builder/AccommodationsBlock.jsx for the parallel
  // implementation triggered from the summary panel.
  // Promote a free-text service row (e.g. "Hotel Eden · 200€/noche") to a
  // proper Accommodation entry. The original service row is REMOVED from
  // the day timeline because the stay will now appear automatically as a
  // read-only chip on every overlapping day (DayBlock derives those chips
  // from `accommodations[]`). This avoids the old double-counting where
  // both the service AND the accommodation contributed to the total.
  const upsertAccommodationFromService = (dayId, service, dateFrom, dateTo) => {
    if (!service.name || !dateFrom || !dateTo) return;
    const dFrom = new Date(dateFrom);
    const dTo = new Date(dateTo);
    if (isNaN(dFrom) || isNaN(dTo) || dTo < dFrom) return;
    const accId = service.acc_id || uid("acc");
    const nights = Math.max(1, Math.round((dTo - dFrom) / 86400000));
    const typedNightlyIncl = service.unit_price_tax_incl || service.unit_price || 0;
    const typedNightlyExcl = service.unit_price_tax_excl || typedNightlyIncl;

    const accsBefore = itn.accommodations || [];
    const existingAcc = accsBefore.find((a) => a.acc_id === accId);
    const cfg = itn.room_config || [];
    const seedRooms = (existingAcc && existingAcc.rooms && existingAcc.rooms.length > 0)
      ? existingAcc.rooms
      : (cfg.length > 0 ? cfg : [{ room_type: "doble", pax: 2, quantity: 1 }])
          .flatMap((rc) => Array.from({ length: rc.quantity || 1 }, () => ({
            room_id: uid("room"),
            room_type: rc.room_type, pax: rc.pax,
            price_per_night_excl: 0, price_per_night_incl: 0, currency: "EUR",
          })));
    const roomsHaveAnyPrice = seedRooms.some((r) => (r.price_per_night_incl || 0) > 0 || (r.price_per_night_excl || 0) > 0);
    const rooms = seedRooms.map((r) => ({
      ...r,
      price_per_night_excl: roomsHaveAnyPrice ? r.price_per_night_excl : typedNightlyExcl,
      price_per_night_incl: roomsHaveAnyPrice ? r.price_per_night_incl : typedNightlyIncl,
    }));
    const numRooms = Math.max(1, rooms.length);
    const sumIncl = rooms.reduce((s, r) => s + (r.price_per_night_incl || r.price_per_night_excl || 0), 0);
    const sumExcl = rooms.reduce((s, r) => s + (r.price_per_night_excl || r.price_per_night_incl || 0), 0);
    const avgIncl = Math.round((sumIncl / numRooms) * 100) / 100;
    const avgExcl = Math.round((sumExcl / numRooms) * 100) / 100;
    const totalQty = nights * numRooms;
    const totalIncl = Math.round(avgIncl * totalQty * 100) / 100;
    const totalExcl = Math.round(avgExcl * totalQty * 100) / 100;
    const baseName = (service.name || "").replace(/^Check-in · |^Check-out · |^Alojamiento · /, "");

    // Strip BOTH the source service and any stale carriers for this acc_id
    // from EVERY day. The new chips will be re-derived from accommodations.
    const newDays = (itn.days || []).map((d) => ({
      ...d,
      services: (d.services || []).filter((s) =>
        s.service_id !== service.service_id && s.acc_id !== accId
      ),
    }));

    const accs = [...accsBefore];
    const existingIdx = accs.findIndex((a) => a.acc_id === accId);
    const accRow = {
      acc_id: accId, name: baseName,
      date_from: dateFrom, date_to: dateTo,
      rooms,
      price_tax_excl: totalExcl, price_tax_incl: totalIncl, price: totalIncl,
      currency: "EUR",
    };
    if (existingIdx === -1) accs.push(accRow);
    else accs[existingIdx] = { ...accs[existingIdx], ...accRow };

    schedSave({ ...itn, days: newDays, accommodations: accs });
  };

  // Drag & drop: move a service between days or reorder within a day.
  const onDragStart = (srcDayId, srcServiceId) => { dragRef.current = { srcDayId, srcServiceId }; };
  const onDropService = (targetDayId, targetIndex) => {
    const src = dragRef.current;
    dragRef.current = null;
    if (!src) return;
    const { srcDayId, srcServiceId } = src;
    if (srcDayId === targetDayId && targetIndex === -1) return;
    let dragged = null;
    const days = itn.days.map((d) => {
      if (d.day_id !== srcDayId) return d;
      const services = [];
      for (const s of d.services || []) {
        if (s.service_id === srcServiceId) dragged = s;
        else services.push(s);
      }
      return { ...d, services };
    });
    if (!dragged) return;
    const finalDays = days.map((d) => {
      if (d.day_id !== targetDayId) return d;
      const services = [...(d.services || [])];
      if (targetIndex < 0 || targetIndex >= services.length) services.push(dragged);
      else services.splice(targetIndex, 0, dragged);
      return { ...d, services };
    });
    schedSave({ ...itn, days: finalDays });
    setActiveDayId(targetDayId);
  };
  const removeService = (dayId, sid) =>
    schedSave({ ...itn, days: itn.days.map((d) => d.day_id === dayId
      ? { ...d, services: d.services.filter((s) => s.service_id !== sid) } : d) });

  const exportXlsx = async () => {
    try {
      const res = await fetch(`${API_BASE}/itineraries/${id}/export`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${(itn.name || "itinerario").replace(/\s+/g, "_")}.xlsx`;
      link.click(); URL.revokeObjectURL(link.href);
      toast.success("Excel descargado");
    } catch (_e) { toast.error("No se pudo exportar"); }
  };

  return (
    <div className="min-h-screen">
      {/* Full-width header — name, status, action buttons, trip metadata, notes */}
      <div className="px-8 pt-6 pb-2">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-xs smallcaps hover:text-terracotta" data-testid="back-dashboard">
          <ArrowLeft size={14} /> Itinerarios
        </button>

        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <input
              data-testid="itn-name-input"
              className="font-serif text-4xl leading-none bg-transparent border-b border-clay-200 outline-none w-full px-1 py-1 hover:border-clay-400 focus:border-terracotta transition-colors"
              value={itn.name || ""}
              placeholder="Nombre del itinerario…"
              onChange={(e) => setField("name", e.target.value)}
              onBlur={flushSave}
            />
            <div className="smallcaps mt-2 flex items-center gap-3 flex-wrap">
              <span data-testid="save-state">{saving ? "Guardando…" : "Guardado"}</span>
              <span>·</span>
              <select data-testid="itn-status" value={itn.status} onChange={(e) => setField("status", e.target.value)} className="bg-transparent border border-clay-300 px-2 py-0.5 text-[10px] uppercase tracking-widest">
                <option value="draft">Borrador</option>
                <option value="sold">Vendido</option>
                <option value="not_sold">No vendido</option>
                <option value="extras">Extras</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setPaymentModalOpen(true)}
                    data-testid="payment-link-btn"
                    title="Generar enlace de pago PayPal para el cliente"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 hover:bg-clay-100 text-sm">
              <CreditCard size={14} /> Enlace de pago
            </button>
            <button onClick={() => setExtrasModalOpen(true)}
                    data-testid="extras-btn"
                    title="Añadir actividades extra post-venta (nuevo enlace de pago)"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 hover:bg-clay-100 text-sm">
              <Sparkles size={14} /> Extras
            </button>
            <button onClick={() => setRefundsModalOpen(true)}
                    data-testid="refunds-btn"
                    title="Solicitar reembolsos (aprobación requerida)"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 hover:bg-clay-100 text-sm">
              <Undo2 size={14} /> Reembolsos
            </button>
            {/* "Exportar Excel" + "Vista previa Sofi" are hidden for now — the
                team works directly off Sofi's UI. Keep the handlers/imports
                wired so we can re-enable them with a single line if needed.
            <button onClick={exportXlsx} data-testid="export-xlsx" className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 hover:bg-clay-100 text-sm">
              <FileDown size={14} /> Exportar Excel
            </button>
            */}
            {itn.sofi_trip_id ? (
              <div className="inline-flex items-stretch border border-pine-soft bg-pine-soft/30">
                <a
                  href={itn.sofi_url || `https://gestion.viajadverdad.com/trips/details/1/${itn.sofi_trip_id}`}
                  target="_blank" rel="noreferrer"
                  data-testid="sofi-already-pushed-link"
                  title={`Enviado a Sofi el ${itn.sofi_pushed_at?.slice(0, 10) || "(fecha no registrada)"}`}
                  className="inline-flex items-center gap-2 px-4 py-2 hover:bg-pine-soft/50 text-sm text-pine"
                >
                  <ExternalLink size={14} /> En Sofi #{itn.sofi_trip_id}
                </a>
                {/* Re-push escape hatch: if the Sofi trip was deleted on
                    their end, the agent needs to unlink here so the main
                    "Enviar a Sofi" button comes back. Introduced 2026-09-14
                    after Susan Hayden trip was accidentally deleted from
                    Sofi and the agent couldn't re-push. */}
                <button
                  onClick={async () => {
                    const ok = window.confirm(
                      `¿Desvincular este itinerario de Sofi trip #${itn.sofi_trip_id}?\n\n` +
                      "Úsalo sólo si el trip ha sido borrado en Sofi por error. " +
                      "Al confirmar podrás volver a pulsar «Enviar a Sofi» y se creará un trip NUEVO."
                    );
                    if (!ok) return;
                    try {
                      const { data } = await api.post(
                        `/itineraries/${id}/sofi-unlink`,
                        { confirm: true },
                      );
                      setItn(data);
                      toast.success("Desvinculado — ya puedes volver a enviarlo a Sofi");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "No se pudo desvincular");
                    }
                  }}
                  data-testid="sofi-unlink-btn"
                  title="Desvincular (marcar como no enviado a Sofi)"
                  className="px-2 border-l border-pine-soft hover:bg-pine-soft/50 text-pine text-xs"
                >
                  ↺
                </button>
              </div>
            ) : (
              <button onClick={() => setSofiModal({ open: true, dryRun: false })}
                      data-testid="sofi-push-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-pine text-white hover:bg-pine-hover text-sm">
                <Send size={14} /> Enviar a Sofi
              </button>
            )}
          </div>
        </div>

        {/* Payment-recovery banner — surfaces only when THIS trip has no
            captured payments but a sibling version does (2026-09-14, after
            Susan Hayden). Lets the agent recover misplaced payments in one
            click, no curl. */}
        {paymentSiblings.length > 0 && (
          <div
            data-testid="payment-recovery-banner"
            className="mt-4 border-2 border-amber-400 bg-amber-50 px-4 py-3"
          >
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="text-lg leading-none mt-0.5">🔎</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-amber-900 text-sm">
                  Este itinerario no tiene pagos, pero otra versión sí
                </div>
                <div className="text-amber-800 text-xs mt-1">
                  Suele pasar cuando duplicas un viaje: los pagos se mueven a la copia. Puedes recuperarlos aquí con un clic.
                </div>
                <div className="mt-3 space-y-2">
                  {paymentSiblings.map((s) => (
                    <div
                      key={s.itinerary_id}
                      data-testid={`payment-sibling-${s.itinerary_id}`}
                      className="flex items-center justify-between gap-3 bg-white border border-amber-300 px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm tabular font-serif">
                          <strong className="text-pine">{s.total_captured_display}</strong>
                          <span className="text-clay-700"> · {s.captured_count} pago{s.captured_count === 1 ? "" : "s"}</span>
                        </div>
                        <div className="text-[11px] text-clay-600 truncate">
                          v{s.version} · {s.name}
                          {s.captured_at && ` · ${s.captured_at.slice(0, 10)}`}
                        </div>
                      </div>
                      <button
                        data-testid={`move-payments-from-${s.itinerary_id}`}
                        disabled={movingPayments}
                        onClick={async () => {
                          const ok = window.confirm(
                            `Traer los pagos de "v${s.version} · ${s.name}" (${s.total_captured_display}) a este itinerario.\n\n` +
                            `Después de traerlos, la otra versión quedará sin pagos ni enlace público (los tendrás sólo aquí).\n\n` +
                            "¿Continuar?"
                          );
                          if (!ok) return;
                          setMovingPayments(true);
                          try {
                            await api.post(
                              `/itineraries/${itn.itinerary_id}/move-payment-state`,
                              { from_itinerary_id: s.itinerary_id, confirm: true },
                            );
                            toast.success("Pagos recuperados correctamente");
                            await load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "No se pudo recuperar");
                          } finally {
                            setMovingPayments(false);
                          }
                        }}
                        className="shrink-0 px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white text-xs uppercase tracking-wider disabled:opacity-60"
                      >
                        {movingPayments ? "Moviendo…" : "Traer aquí"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trip metadata — Row 1: who/when (5 cols) · Row 2: pax breakdown */}
        <div className="mt-6 border border-clay-300">
          <div className="grid grid-cols-7 gap-0">
            <Field label="Viajero principal">
              <input data-testid="main-traveler" className="w-full bg-transparent outline-none text-sm" value={itn.main_traveler || ""} onChange={(e) => setField("main_traveler", e.target.value)} placeholder="Nombre Apellido" />
            </Field>
            <Field label="Partner">
              <PartnerSelector itn={itn} setField={setField} />
            </Field>
            <Field label="Propietario">
              <OwnerSelector itn={itn} setField={setField} />
            </Field>
            <Field label="Agente de ventas">
              <SalesAgentSelector itn={itn} setField={setField} />
            </Field>
            <Field label="Inicio">
              <input data-testid="start-date" type="date" className="w-full bg-transparent outline-none text-sm tabular" value={itn.start_date || ""} onChange={(e) => setField("start_date", e.target.value)} />
            </Field>
            <Field label="Fin">
              <input data-testid="end-date" type="date" className="w-full bg-transparent outline-none text-sm tabular" value={itn.end_date || ""} onChange={(e) => setField("end_date", e.target.value)} />
            </Field>
            <Field label="Noches">
              <div className="flex items-center gap-1.5 text-sm tabular" data-testid="nights-readout">
                <Moon size={13} className="text-clay-500" />
                <span>{itn.start_date && itn.end_date ? Math.max(0, daysBetween(itn.start_date, itn.end_date) - 1) : "—"}</span>
              </div>
            </Field>
          </div>
          {/* Row 2: PAX breakdown — adults / children / ages */}
          {(() => {
            const adults = itn.num_adults ?? itn.num_travelers ?? 2;
            const children = itn.num_children ?? 0;
            const ages = itn.children_ages || [];
            const totalPax = adults + children;
            const setAdults = (v) => {
              const a = Math.max(1, parseInt(v || "0", 10) || 0);
              schedSave({ ...itn, num_adults: a, num_travelers: a + children });
            };
            const setChildren = (v) => {
              const c = Math.max(0, parseInt(v || "0", 10) || 0);
              const nextAges = c <= ages.length
                ? ages.slice(0, c)
                : [...ages, ...Array(c - ages.length).fill(0)];
              schedSave({ ...itn, num_children: c, children_ages: nextAges, num_travelers: adults + c });
            };
            const setAge = (idx, v) => {
              const next = [...ages];
              next[idx] = Math.max(0, Math.min(17, parseInt(v || "0", 10) || 0));
              schedSave({ ...itn, children_ages: next });
            };
            return (
              <div className="grid grid-cols-[1fr_1fr_2fr_1fr] gap-0 border-t border-clay-300">
                <Field label="Adultos">
                  <input data-testid="num-adults" type="number" min={1} max={20}
                         className="w-full bg-transparent outline-none text-sm tabular"
                         value={adults}
                         onChange={(e) => setAdults(e.target.value)} />
                </Field>
                <Field label="Niños">
                  <input data-testid="num-children" type="number" min={0} max={10}
                         className="w-full bg-transparent outline-none text-sm tabular"
                         value={children}
                         onChange={(e) => setChildren(e.target.value)} />
                </Field>
                <Field label={children > 0 ? "Edades niños" : "Edades niños (sin niños)"}>
                  {children > 0 ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="children-ages-row">
                      {Array.from({ length: children }).map((_, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="text-[10px] uppercase tracking-wider text-clay-500">#{i + 1}</span>
                          <input
                            data-testid={`child-age-${i}`}
                            type="number" min={0} max={17}
                            value={ages[i] ?? 0}
                            onChange={(e) => setAge(i, e.target.value)}
                            className="w-12 bg-transparent border border-clay-300 px-1 py-0.5 text-sm tabular text-center"
                          />
                          <span className="text-[10px] text-clay-500">a.</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-clay-500 italic">—</span>
                  )}
                </Field>
                <Field label="Pax (total)">
                  <div className="text-sm tabular font-medium" data-testid="pax-total">{totalPax}</div>
                </Field>
              </div>
            );
          })()}
        </div>

        {/* Notes — full width */}
        <div className="mt-4 border border-clay-300">
          <div className="px-4 py-2 bg-clay-100 border-b border-clay-300 smallcaps">
            Notas internas
          </div>
          <textarea
            data-testid="itin-notes"
            value={itn.notes || ""}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Recordatorios, preferencias del cliente, condiciones especiales, contexto para otros agentes…"
            rows={2}
            className="w-full bg-white outline-none text-sm px-4 py-3 resize-y min-h-[60px] focus:bg-amber-50/30"
          />
        </div>
      </div>

      {/* Below header: 2-col grid (left = room config + days + accommodations, right = costs panel) */}
      <div className="grid grid-cols-[1fr_380px]">
        <div className="px-8 pt-2 pb-6 border-r border-clay-300">
          {/* Default room configuration */}
          <div className="mt-2">
            <RoomConfigEditor
              config={itn.room_config || []}
              numTravelers={itn.num_travelers}
              onChange={(next) => schedSave({ ...itn, room_config: next })}
            />
          </div>

          {/* Days */}
          <div className="mt-8 space-y-6">
            {/* Top drop zone — allows dragging a day to become the FIRST
                day of the trip. Positioned above the first DayBlock. */}
            {(itn.days || []).length > 0 && (
              <DayGap
                position={0}
                onDayDrop={onDayDrop}
                onInsertBefore={() => insertDayAfter(null)}
                showInsertHere={false}
              />
            )}
            {(itn.days || []).map((day, idx) => (
              <React.Fragment key={day.day_id}>
                <DayBlock
                  day={day}
                  idx={idx}
                  totalDays={(itn.days || []).length}
                  active={activeDayId === day.day_id}
                  numTravelers={itn.num_travelers}
                  accommodations={itn.accommodations || []}
                  cityFacets={facets.cities}
                  markup={itn.markup_pct || 0}
                  onActivate={() => setActiveDayId(day.day_id)}
                  onUpdateDay={(patch) => updateDay(day.day_id, patch)}
                  onAddBlank={() => addServiceToDay(day.day_id)}
                  onRemoveDay={() => removeDay(day.day_id)}
                  onUpdateService={(sid, patch) => updateService(day.day_id, sid, patch)}
                  onRemoveService={(sid) => removeService(day.day_id, sid)}
                  onDragStart={onDragStart}
                  onDropService={onDropService}
                  onOrient={openOrient}
                  onAccommodate={(svc, dFrom, dTo) => upsertAccommodationFromService(day.day_id, svc, dFrom, dTo)}
                  onDayDragStart={() => onDayDragStart(idx)}
                  onMoveUp={() => moveDay(day.day_id, "up")}
                  onMoveDown={() => moveDay(day.day_id, "down")}
                />
                {/* Gap between days: drop zone (for day reorder) +
                    hover-visible "+ insert day here" button. */}
                <DayGap
                  position={idx + 1}
                  onDayDrop={onDayDrop}
                  onInsertBefore={() => insertDayAfter(day.day_id)}
                />
              </React.Fragment>
            ))}
            <button onClick={addDay} data-testid="add-day-btn" className="w-full border border-dashed border-clay-300 py-3 text-sm text-clay-700 hover:border-terracotta hover:text-terracotta transition-colors">
              <Plus size={14} className="inline mr-2"/> Añadir día al final
            </button>
          </div>

          <AccommodationsBlock itn={itn} schedSave={schedSave} markup={itn.markup_pct || 0} onOrient={openOrient} />

          {/* Post-sale accounting: renders below the itinerary body once
              the trip has activity to show (paid extras or refund requests).
              Reads from `itn.extras` and `itn.refund_requests` and mirrors
              the modals so agents don't have to open a dialog to see the
              audit trail. */}
          {((itn.extras || []).length > 0 || (itn.refund_requests || []).length > 0) && (
            <PostSaleSection
              extras={itn.extras || []}
              refunds={itn.refund_requests || []}
              payments={itn.payments || []}
              onOpenExtras={() => setExtrasModalOpen(true)}
              onOpenRefunds={() => setRefundsModalOpen(true)}
              sofiTripId={itn.sofi_trip_id}
            />
          )}
        </div>

        {/* Right: cost summary — sticky container with internal overflow
            so the pricing stays visible while scrolling the days AND the
            long payment history / cashflow block below is reachable via
            an internal scrollbar when its content exceeds the viewport. */}
        <aside className="bg-clay-50/60">
          <div className="sticky top-2 max-h-[95vh] overflow-y-auto flex flex-col">
            <div className="border-b border-clay-300 p-5 bg-white">
              <button type="button"
                      onClick={toggleCost}
                      data-testid="cost-collapse-toggle"
                      className="w-full flex items-center justify-between text-left group">
                <div className="smallcaps">Coste</div>
                <span className="text-clay-500 group-hover:text-clay-700 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
                  {costCollapsed ? "Ampliar" : "Colapsar"}
                  {costCollapsed ? <ChevronDown size={12}/> : <ChevronUp size={12}/>}
                </span>
              </button>
              <div className="grid-borders mt-3">
                {!costCollapsed && (
                  <>
                <Row label="Subtotal con IVA">{fmtEUR(totals.sub_incl)}</Row>
                <Row label={(
                  <div className="flex items-center gap-2">
                    <span>Markup</span>
                    <input data-testid="markup-input" type="number" step="0.5" min="0"
                      value={itn.markup_pct ?? 0}
                      onChange={(e) => setField("markup_pct", parseFloat(e.target.value || "0"))}
                      className="w-16 bg-transparent border border-clay-300 px-1 py-0.5 text-sm tabular text-right" />
                    <span className="text-xs text-clay-700">%</span>
                  </div>
                )}>+ {fmtEUR(totals.markup_eur)}</Row>
                <Row label="Subtotal con markup">{fmtEUR(totals.sub_with_markup)}</Row>
                {(itn.commission_pct ?? 0) > 0 && (
                  <Row label={(
                    <div className="flex items-center gap-2">
                      <span>Comisión <span className="text-clay-500 normal-case">({partnerLabelOf(itn.partner) || itn.partner})</span></span>
                      <input
                        data-testid="commission-input"
                        type="number" step="0.5" min="0"
                        value={itn.commission_pct ?? 0}
                        onChange={(e) => setField("commission_pct", parseFloat(e.target.value || "0"))}
                        className="w-16 bg-transparent border border-clay-300 px-1 py-0.5 text-sm tabular text-right"
                      />
                      <span className="text-xs text-clay-700">%</span>
                      <button
                        data-testid="auto-partner-defaults"
                        onClick={applyPartnerDefaults}
                        className="p-0.5 hover:bg-clay-200 text-clay-600 hover:text-terracotta inline-flex items-center gap-0.5"
                        title={`Auto: markup ${partnerDefaultsOf(itn.partner)?.markup_pct}% · comisión ${partnerDefaultsOf(itn.partner)?.commission_pct}%`}
                      >
                        <RotateCw size={11}/>
                        <span className="text-[9px] uppercase tracking-wider">Auto</span>
                      </button>
                    </div>
                  )}>+ {fmtEUR(totals.commission_eur)}</Row>
                )}
                {/* PayPal Fee toggle — adds 3% on top of the otherwise final PVP */}
                <div className="flex items-center justify-between py-2 px-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="paypal-fee-toggle"
                      checked={!!itn.paypal_fee}
                      onChange={(e) => setField("paypal_fee", e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span>PayPal Fee <span className="text-clay-500">(+{PAYPAL_FEE_PCT}%)</span></span>
                  </label>
                  <span className="tabular text-clay-700">{itn.paypal_fee ? `+ ${fmtEUR(totals.paypal_eur)}` : "—"}</span>
                </div>
                  </>
                )}
                <div className="flex items-center justify-between py-3 bg-clay-900 text-white px-3 mt-2" data-testid="final-price">
                  <div className="smallcaps text-white/70">PVP final</div>
                  <div className="font-serif text-2xl tabular">{fmtEUR(totals.pvp)}</div>
                </div>
                {/* Post-sale accounting — only render when there's actually
                    an extra paid or a refund executed. Keeps the summary
                    clean for draft trips. */}
                {(totals.extras_paid > 0 || totals.refunds_executed > 0) && (
                  <div className="mt-2 border border-clay-300"
                       data-testid="post-sale-summary">
                    <div className="px-3 py-2 border-b border-clay-300 bg-clay-50 smallcaps inline-flex items-center gap-2">
                      <Sparkles size={11}/> Ajustes post-venta
                    </div>
                    {totals.extras_paid > 0 && (
                      <div className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <span className="text-clay-700">Extras cobrados</span>
                        <span className="tabular text-pine">+ {fmtEUR(totals.extras_paid)}</span>
                      </div>
                    )}
                    {totals.refunds_executed > 0 && (
                      <div className="flex items-center justify-between px-3 py-1.5 text-sm border-t border-clay-300">
                        <span className="text-clay-700">Reembolsos ejecutados</span>
                        <span className="tabular text-red-700">− {fmtEUR(totals.refunds_executed)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-clay-300 bg-terracotta-soft/30"
                         data-testid="pvp-adjusted">
                      <div className="smallcaps text-clay-700">PVP ajustado</div>
                      <div className="font-serif text-xl tabular text-terracotta">{fmtEUR(totals.pvp_adjusted)}</div>
                    </div>
                  </div>
                )}
                <FxConverter
                  fx={fx}
                  setFx={setFx}
                  totals={totals}
                  onPersist={(rate) => setField("fx_rate", rate)}
                />
              </div>
            </div>
            <CashflowStatus
              startDate={itn.start_date}
              total={totals.pvp_adjusted || totals.pvp}
              payments={itn.payments || []}
              onOpenLinkModal={() => setPaymentModalOpen(true)}
            />
          </div>
        </aside>
      </div>

      {orient && (
        <OrientationModal
          city={orient.city}
          hotelName={orient.hotelName}
          checkin={orient.checkin}
          checkout={orient.checkout}
          adults={orient.adults}
          busy={orient.busy}
          data={orient.data}
          onClose={() => setOrient(null)}
          onApply={(price) => { if (orient.onApply) orient.onApply(price); setOrient(null); }}
        />
      )}

      <SofiPushModal
        key={`${sofiModal.open}-${sofiModal.dryRun}`}
        open={sofiModal.open}
        dryRun={sofiModal.dryRun}
        itineraryId={id}
        onClose={() => setSofiModal({ open: false, dryRun: true })}
        onSwitchToReal={() => setSofiModal({ open: true, dryRun: false })}
        onPushed={(res) => {
          // Real push succeeded — refetch the itinerary so the badge swaps to
          // "En Sofi #N" and the Vista previa / Enviar buttons disappear.
          api.get(`/itineraries/${id}`).then(({ data }) => setItn(data)).catch(() => {});
          toast.success(`Itinerario enviado a Sofi #${res.trip_id}`);
        }}
      />

      <PaymentLinkModal
        open={paymentModalOpen}
        itineraryId={id}
        onClose={() => setPaymentModalOpen(false)}
      />

      <ExtrasModal
        open={extrasModalOpen}
        itineraryId={id}
        days={itn.days || []}
        onClose={() => setExtrasModalOpen(false)}
      />

      <RefundsModal
        open={refundsModalOpen}
        itineraryId={id}
        onClose={() => setRefundsModalOpen(false)}
      />
    </div>
  );
}
